import { faGear } from "@fortawesome/free-solid-svg-icons/faGear";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import camelCase from "lodash/camelCase";
import { faGlobe } from "@fortawesome/free-solid-svg-icons/faGlobe";
import { faBell } from "@fortawesome/free-solid-svg-icons/faBell";
import { faSliders } from "@fortawesome/free-solid-svg-icons/faSliders";
import { faCubes } from "@fortawesome/free-solid-svg-icons/faCubes";
import { faPalette } from "@fortawesome/free-solid-svg-icons/faPalette";
import { faLock } from "@fortawesome/free-solid-svg-icons/faLock";
import { faTags } from "@fortawesome/free-solid-svg-icons/faTags";

import { PageWithSideMenu } from "../../components/PageWithSideMenu";
import { SpinnerFullScreen } from "../../components/SpinnerFullScreen";

import { UpdateUserSettingsDto } from "../../model/user/settings/dto/UpdateUserSettingsDto";
import { settingsActions } from "../../redux/settings/settings.slice";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import { userActions } from "../../redux/user/user.slice";
import { userSliceSelectors } from "../../redux/user/user.selector";
import { AtiraToast } from "../../utils/AtiraToast";
import { onSetTypedCRUDErrors } from "../../utils/onSetTypedCRUDErrors";
import { SettingsGeneral } from "./sections/SettingsGeneral";
import { SettingsLanguage } from "./sections/SettingsLanguage";
import { SettingsNotifications } from "./sections/SettingsNotifications";
import { SettingsFields } from "./sections/SettingsFields";
import { SettingsSubRoutes } from "../../model/app/types/SettingsSubRoutes.enum";
import { SettingsModules } from "./sections/SettingsModules";
import { SettingsTags } from "./sections/SettingsTags";
import { SettingsAppearance } from "./sections/SettingsAppearance";
import { SignupMethod } from "../../model/user/types/SignupMethod";
import { SettingsSecurityAndPassword } from "./sections/SettingsSecurityAndPassword";
import { faUserTag } from "@fortawesome/free-solid-svg-icons/faUserTag";
import { SettingsManageAccounts } from "./sections/SettingsManageAccounts";
import { AppLangs } from "../../model/shared/enum/AppLangs.enum";
import { SettingsGoogleAccountSetPassword } from "./sections/SettingsGoogleAccountSetPassword";
import { SettingsTwoFactor } from "./sections/SettingsTwoFactor";
import { SettingsSupportAccess } from "./sections/SettingsSupportAccess";
import { faRobot } from "@fortawesome/free-solid-svg-icons/faRobot";
import { SettingsAIChatBot } from "./sections/SettingsAIChatBot";
import { settingsSliceSelectors } from "../../redux/settings/settings.selector";
import omitBy from "lodash/omitBy";
import isNil from "lodash/isNil";
import isEmpty from "lodash/isEmpty";
import { isImpersonation } from "../../redux/store-utils";

const getSettingsRouteIcon = (route: SettingsSubRoutes) => {
  switch (route) {
    case SettingsSubRoutes.GENERAL:
      return faGear;
    case SettingsSubRoutes.LANGUAGE:
      return faGlobe;
    case SettingsSubRoutes.NOTIFICATIONS:
      return faBell;
    case SettingsSubRoutes.FIELDS:
      return faSliders;
    case SettingsSubRoutes.MODULES:
      return faCubes;
    case SettingsSubRoutes.TAGS:
      return faTags;
    case SettingsSubRoutes.APPEARANCE:
      return faPalette;
    case SettingsSubRoutes.SECURITY_PASSWORD:
      return faLock;
    case SettingsSubRoutes.MANAGE_ACCOUNTS:
      return faUserTag;
    case SettingsSubRoutes.AI_CHATBOT:
      return faRobot;
  }
};

export const Settings: React.FC = () => {
  const dispatch = useAppDispatch();
  const { t, i18n } = useTranslation();
  const { section } = useParams();
  const formMethods = useForm<UpdateUserSettingsDto>();
  const [settingsHydrated, setSettingsHydrated] = useState(false);

  const settings = useAppSelector(settingsSliceSelectors.selectSettings);
  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;
  const isOrgMember = useAppSelector(userSliceSelectors.selectIsUserOrgMember);
  const user = useAppSelector(userSliceSelectors.selectLoggedInUser)!;

  const { getValues, setError, handleSubmit } = formMethods;

  const onSave = useCallback(async () => {
    try {
      const dto = getValues();

      await dispatch(
        settingsActions.updateSettings({
          ...(omitBy(
            dto,
            (value) => isNil(value) || isEmpty(value),
          ) as UpdateUserSettingsDto),
          notifications: formMethods.getValues("notifications"),
        }),
      ).unwrap();

      const [updatedSettings] = await Promise.all([
        dispatch(settingsActions.getSettings({ userId })).unwrap(),
        dispatch(userActions.getLoggedInUser({ userId })).unwrap(),
      ]);

      formMethods.reset({
        timezone: updatedSettings?.timezone || "",
        lang: i18n.language.toLowerCase() as AppLangs,
        notifications: { ...(updatedSettings?.notifications || {}) },
        currency: updatedSettings?.currency || "",
        userId,
      });

      AtiraToast.success(t("settings.update.success"));

      if (dto.lang) {
        setTimeout(() => {
          i18n.changeLanguage(dto.lang);
        }, 500);
      }
    } catch (e) {
      console.log(e);
      AtiraToast.apiError(e);
      onSetTypedCRUDErrors(e, setError);
    }
  }, [dispatch, getValues, i18n, setError, settings, t, userId, formMethods]);

  const sections = useMemo(() => {
    return Object.values(SettingsSubRoutes).map((route) => {
      const link = `/settings/${route}`;
      const title = t(`settings.${camelCase(route)}.title`);
      const icon = getSettingsRouteIcon(route);

      const shouldSetPasswordForGoogleAccount =
        user.signupMethod === SignupMethod.GOOGLE && user?.hasPassword !== true;
      const isImpersonating = isImpersonation();

      let section: React.ReactNode = null;
      let hide = false;

      switch (route) {
        case SettingsSubRoutes.GENERAL:
          section = <SettingsGeneral onSave={handleSubmit(onSave)} />;
          break;

        case SettingsSubRoutes.LANGUAGE:
          section = <SettingsLanguage onSave={handleSubmit(onSave)} />;
          break;

        case SettingsSubRoutes.NOTIFICATIONS:
          section = <SettingsNotifications onSave={handleSubmit(onSave)} />;
          break;

        case SettingsSubRoutes.MANAGE_ACCOUNTS:
          section = <SettingsManageAccounts />;
          break;

        case SettingsSubRoutes.MODULES:
          section = <SettingsModules />;
          break;

        case SettingsSubRoutes.FIELDS:
          section = <SettingsFields />;
          break;

        case SettingsSubRoutes.TAGS:
          section = <SettingsTags />;
          break;

        case SettingsSubRoutes.APPEARANCE:
          section = <SettingsAppearance />;
          break;

        case SettingsSubRoutes.SECURITY_PASSWORD:
          // A Google account with no password can't password-gate 2FA changes,
          // so it only sees the "set a password" panel until it has one.
          section = (
            <>
              {shouldSetPasswordForGoogleAccount ? (
                <SettingsGoogleAccountSetPassword />
              ) : (
                <>
                  <SettingsSecurityAndPassword />
                  <SettingsTwoFactor />
                </>
              )}
              <SettingsSupportAccess />
            </>
          );
          break;

        case SettingsSubRoutes.AI_CHATBOT:
          section = <SettingsAIChatBot />;

          break;
      }

      if (
        isOrgMember &&
        [SettingsSubRoutes.FIELDS, SettingsSubRoutes.MODULES].includes(route)
      ) {
        hide = true;
      }

      if (isImpersonating && route === SettingsSubRoutes.MANAGE_ACCOUNTS) {
        hide = true;
      }

      return {
        section,
        link,
        title,
        icon,
        hide,
      };
    });
  }, [
    handleSubmit,
    isOrgMember,
    onSave,
    t,
    user.signupMethod,
    user?.hasPassword,
  ]);

  useEffect(() => {
    let isMounted = true;

    const loadSettings = async () => {
      try {
        setSettingsHydrated(false);

        const loadedSettings = await dispatch(
          settingsActions.getSettings({ userId }),
        ).unwrap();

        if (!isMounted) {
          return;
        }

        formMethods.reset({
          timezone: loadedSettings?.timezone || "",
          lang: i18n.language.toLowerCase() as AppLangs,
          notifications: { ...(loadedSettings?.notifications || {}) },
          currency: loadedSettings?.currency || "",
          userId,
        });
      } catch (e) {
        console.log(e);
        AtiraToast.apiError(e);
      } finally {
        if (isMounted) {
          setSettingsHydrated(true);
        }
      }
    };

    loadSettings();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, userId]);

  if (!settings || !settingsHydrated) {
    return <SpinnerFullScreen />;
  }

  return (
    <FormProvider {...formMethods}>
      <PageWithSideMenu
        subheaderProps={{
          title: t(`settings.${camelCase(section)}.subheader.title`),
          icon: getSettingsRouteIcon(section as SettingsSubRoutes),
        }}
        sections={sections}
      />
    </FormProvider>
  );
};

export default Settings;
